import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { useMutation } from "@tanstack/react-query";
import { usePlaysList, type PlayListItem } from "../../hooks/usePlay";
import { useTemplates, type TemplateMeta } from "../../hooks/useTemplates";
import { usePersona } from "../../hooks/usePersona";
import { apiPost } from "../../api/client";

type Tab = "planning" | "flight" | "inbox" | "templates" | "completed";

function statusPill(state: string): string {
  if (state === "running") return "green";
  if (state === "paused") return "amber";
  if (state === "replanning") return "purple";
  if (state === "completed") return "blue";
  if (state === "abandoned") return "red";
  return "gray";
}

function PlayCard({
  play,
  onOpen,
}: {
  play: PlayListItem;
  onOpen: (id: string) => void;
}) {
  const title =
    typeof play["title"] === "string"
      ? (play["title"] as string)
      : play.archetype;
  const lastEvent =
    typeof play["lastEvent"] === "string" ? (play["lastEvent"] as string) : "";
  return (
    <div
      className="play-card"
      data-testid={`plays-index-card-${play.id}`}
      onClick={() => onOpen(play.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen(play.id);
      }}
      style={{ cursor: "pointer" }}
    >
      <div className="play-arch">{play.archetype}</div>
      <div style={{ fontSize: 15, fontWeight: 600, marginTop: 4 }}>{title}</div>
      {lastEvent ? (
        <div
          style={{
            fontSize: 11.5,
            color: "var(--text-3)",
            marginTop: 6,
          }}
        >
          {lastEvent}
        </div>
      ) : null}
      <div
        style={{
          display: "flex",
          gap: 6,
          marginTop: 10,
          flexWrap: "wrap",
        }}
      >
        <span className={`pill ${statusPill(play.state)}`}>{play.state}</span>
        {play.replanCount > 0 ? (
          <span className="pill purple">⟲ {play.replanCount}</span>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// New Play modal
// ---------------------------------------------------------------------------

function NewPlayModal({
  workspaceId,
  onClose,
  onCreated,
}: {
  workspaceId: string;
  onClose: () => void;
  onCreated: (playId: string) => void;
}) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<TemplateMeta | null>(null);
  const [customArchetype, setCustomArchetype] = useState("");
  const [useCustom, setUseCustom] = useState(false);

  const templatesQ = useTemplates();
  const templates = templatesQ.data?.templates ?? [];

  const filtered = templates.filter((t) => {
    const q = search.toLowerCase();
    return (
      !q ||
      t.archetype.toLowerCase().includes(q) ||
      t.name.toLowerCase().includes(q) ||
      t.trade.toLowerCase().includes(q) ||
      t.industry.toLowerCase().includes(q)
    );
  });

  const createPlay = useMutation({
    mutationFn: (archetype: string) =>
      apiPost<{ play: { id: string } }>(`/api/workspaces/${workspaceId}/plays`, {
        archetype,
      }),
    onSuccess: (data) => onCreated(data.play.id),
  });

  const canSubmit = useCustom
    ? customArchetype.trim().length > 0
    : selected !== null;

  const handleSubmit = () => {
    if (!canSubmit || createPlay.isPending) return;
    const archetype = useCustom ? customArchetype.trim() : selected!.archetype;
    createPlay.mutate(archetype);
  };

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" style={{ width: 600, maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
        <div className="modal-head">
          <span className="modal-title">New Play</span>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: "20px 26px 4px", display: "flex", gap: 8 }}>
          <button
            type="button"
            className={`btn ${!useCustom ? "primary" : ""}`}
            onClick={() => setUseCustom(false)}
          >
            From template
          </button>
          <button
            type="button"
            className={`btn ${useCustom ? "primary" : ""}`}
            onClick={() => setUseCustom(true)}
          >
            Custom
          </button>
        </div>

        <div style={{ padding: "16px 26px 22px", flex: 1, overflowY: "auto" }}>
          {useCustom ? (
            <div>
              <label style={{ fontSize: 12, color: "var(--text-3)", display: "block", marginBottom: 6 }}>
                Workflow name / archetype
              </label>
              <input
                autoFocus
                className="input"
                placeholder="e.g. Vendor onboarding, Monthly audit, Shift handover"
                value={customArchetype}
                onChange={(e) => setCustomArchetype(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
                style={{ width: "100%" }}
              />
              <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 6 }}>
                A blank play will be created. You can add steps from the play editor.
              </div>
            </div>
          ) : (
            <>
              <input
                autoFocus
                className="input"
                placeholder="Search templates…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: "100%", marginBottom: 10 }}
              />
              {templatesQ.isLoading ? (
                <div style={{ color: "var(--text-3)", fontSize: 13 }}>Loading templates…</div>
              ) : filtered.length === 0 ? (
                <div style={{ color: "var(--text-3)", fontSize: 13 }}>No templates match.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {filtered.map((t) => {
                    const isSelected = selected?.archetype === t.archetype;
                    return (
                      <div
                        key={t.id ?? `${t.industry}::${t.trade}::${t.archetype}`}
                        onClick={() => setSelected(t)}
                        style={{
                          padding: "10px 12px",
                          borderRadius: 8,
                          border: `1px solid ${isSelected ? "var(--accent)" : "var(--border)"}`,
                          background: isSelected ? "var(--accent-soft)" : "white",
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{t.name || t.archetype}</div>
                        <div style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 2 }}>
                          {t.industry} · {t.trade}
                          {t.stepsCount ? ` · ${t.stepsCount} steps` : ""}
                        </div>
                        {t.description ? (
                          <div style={{ fontSize: 11.5, color: "var(--text-2)", marginTop: 4 }}>
                            {t.description.slice(0, 120)}{t.description.length > 120 ? "…" : ""}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-foot">
          {createPlay.isError ? (
            <span style={{ fontSize: 12, color: "var(--red)" }}>
              Failed to create play. Try again.
            </span>
          ) : null}
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn primary"
            disabled={!canSubmit || createPlay.isPending}
            onClick={handleSubmit}
          >
            {createPlay.isPending ? "Creating…" : "Create play"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function PlaysIndexInner() {
  const { workspaceId = "" } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const persona = usePersona(workspaceId);
  const [tab, setTab] = useState<Tab>("flight");
  const [showNewPlay, setShowNewPlay] = useState(false);

  // "Planning" = draft plays — accepted from inbox, brief / outcome /
  // approvals still being finalised. Distinct from "In flight", which
  // is post-approval execution. Splitting these matters: a draft play
  // needs the CXO's attention (approve / start), while an in-flight
  // play is already executing and just needs monitoring.
  const planning = usePlaysList(workspaceId, ["draft"]);
  const inflight = usePlaysList(workspaceId, [
    "approved",
    "running",
    "paused",
    "replanning",
  ]);
  const completed = usePlaysList(workspaceId, ["completed", "abandoned"]);

  const planningItems = planning.data?.items ?? [];
  const inflightItems = inflight.data?.items ?? [];
  const completedItems = completed.data?.items ?? [];

  const open = (id: string) =>
    navigate(`/workspaces/${workspaceId}/plays/${id}`);

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: "planning", label: "Planning", count: planningItems.length },
    { key: "flight", label: "In flight", count: inflightItems.length },
    { key: "inbox", label: "Inbox", count: 0 },
    { key: "templates", label: "Templates", count: 0 },
    { key: "completed", label: "Completed", count: completedItems.length },
  ];

  return (
    <div className="main-inner" data-testid="plays-index">
      <div
        className="page-h"
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 12,
          marginBottom: 4,
        }}
      >
        <span
          className="ttl"
          style={{
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: "-0.018em",
            color: "var(--text)",
          }}
        >
          Plays
        </span>
        <span style={{ fontSize: 12.5, color: "var(--text-3)", flex: 1 }}>
          {planningItems.length === 0
            ? `${inflightItems.length} in flight`
            : inflightItems.length === 0
              ? `${planningItems.length} in planning`
              : `${planningItems.length} in planning · ${inflightItems.length} in flight`}
        </span>
        {persona === "cxo" || persona === "owner" ? (
          <button
            type="button"
            className="btn primary"
            data-testid="plays-new-play-btn"
            onClick={() => setShowNewPlay(true)}
          >
            + New Play
          </button>
        ) : null}
      </div>
      <div
        style={{
          display: "flex",
          gap: 4,
          borderBottom: "1px solid var(--border)",
          marginTop: 14,
          marginBottom: 16,
        }}
      >
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`plays-index-tab-${t.key}`}
            onClick={() => {
              if (t.key === "inbox") {
                navigate(`/workspaces/${workspaceId}/inbox`);
                return;
              }
              setTab(t.key);
            }}
            style={{
              padding: "8px 14px 10px",
              fontSize: 12.5,
              fontWeight: 500,
              background: "transparent",
              border: 0,
              cursor: "pointer",
              color: tab === t.key ? "var(--text)" : "var(--text-3)",
              borderBottom: `2px solid ${tab === t.key ? "var(--accent)" : "transparent"}`,
              marginBottom: -1,
              fontFamily: "inherit",
            }}
          >
            {t.label}{" "}
            <span
              style={{
                color: "var(--text-mute)",
                fontWeight: 400,
                marginLeft: 4,
              }}
            >
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {tab === "planning" ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 12,
          }}
          data-testid="plays-index-planning"
        >
          {planning.isLoading ? (
            <div className="play-card">Loading…</div>
          ) : planning.isError ? (
            <div className="play-card">Couldn't load plays.</div>
          ) : planningItems.length === 0 ? (
            <div className="play-card" data-testid="plays-index-planning-empty">
              <div className="play-title">Nothing in planning.</div>
              <div className="play-arch">
                Plays accepted from the Inbox (or created from a template)
                land here in draft state. Open one to finalise its outcome,
                brief, and approvals — then start it. Once approved + started,
                it moves to In flight.
              </div>
            </div>
          ) : (
            planningItems.map((p) => (
              <PlayCard key={p.id} play={p} onOpen={open} />
            ))
          )}
        </div>
      ) : null}

      {tab === "flight" ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 12,
          }}
          data-testid="plays-index-flight"
        >
          {inflight.isLoading ? (
            <div className="play-card" data-testid="plays-index-skeleton">
              Loading…
            </div>
          ) : inflight.isError ? (
            <div className="play-card" data-testid="plays-index-error">
              Couldn't load plays.
            </div>
          ) : inflightItems.length === 0 ? (
            <div className="play-card" data-testid="plays-index-empty">
              <div className="play-title">No plays in flight.</div>
              <div className="play-arch">
                Approved + started plays show here. Open a Planning play to
                approve + start it, or use + New Play.
              </div>
            </div>
          ) : (
            inflightItems.map((p) => (
              <PlayCard key={p.id} play={p} onOpen={open} />
            ))
          )}
        </div>
      ) : null}

      {tab === "templates" ? (
        <div
          style={{
            padding: "40px 0",
            textAlign: "center",
            color: "var(--text-3)",
            fontSize: 13,
          }}
          data-testid="plays-index-templates"
        >
          Template browser — published patterns. (Deferred to Chunk 9.)
        </div>
      ) : null}

      {tab === "completed" ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 12,
          }}
          data-testid="plays-index-completed"
        >
          {completed.isLoading ? (
            <div className="play-card">Loading…</div>
          ) : completedItems.length === 0 ? (
            <div className="play-card">No completed plays yet.</div>
          ) : (
            completedItems.map((p) => (
              <PlayCard key={p.id} play={p} onOpen={open} />
            ))
          )}
        </div>
      ) : null}

      {showNewPlay ? (
        <NewPlayModal
          workspaceId={workspaceId}
          onClose={() => setShowNewPlay(false)}
          onCreated={(id) => {
            setShowNewPlay(false);
            navigate(`/workspaces/${workspaceId}/plays/${id}`);
          }}
        />
      ) : null}
    </div>
  );
}

function PlaysIndexErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  return (
    <div className="main-inner" data-testid="plays-index-error-boundary">
      <h1>Plays unavailable</h1>
      <div style={{ fontSize: 13, color: "var(--text-3)" }}>
        {error.message}
      </div>
      <button
        type="button"
        className="btn primary"
        style={{ marginTop: 12 }}
        onClick={resetErrorBoundary}
      >
        Reload surface
      </button>
    </div>
  );
}

export function PlaysIndex() {
  return (
    <ErrorBoundary FallbackComponent={PlaysIndexErrorFallback}>
      <PlaysIndexInner />
    </ErrorBoundary>
  );
}
